import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, InputNumber, Select, Switch, Space, Tag, Popconfirm, message, Alert, Tooltip,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, MinusCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import { CountryFlagList } from '../../../shared/components/CountryFlag';

const FIELDS = [
  { value: 'IP', label: 'IP 地址' },
  { value: 'COUNTRY', label: '国家代码' },
  { value: 'ASN', label: 'ASN' },
  { value: 'HOST', label: '主机名' },
  { value: 'URI', label: 'URI 路径' },
  { value: 'PATH', label: '路径' },
  { value: 'EXTENSION', label: '扩展名' },
  { value: 'METHOD', label: '请求方法' },
  { value: 'USER_AGENT', label: 'User-Agent' },
  { value: 'REFERER', label: 'Referer' },
  { value: 'HEADER', label: '请求头' },
  { value: 'COOKIE', label: 'Cookie' },
  { value: 'QS_ARGS', label: '查询参数' },
  { value: 'PROTOCOL', label: '协议' },
  { value: 'JA3', label: 'JA3 指纹' },
  { value: 'JA4', label: 'JA4 指纹' },
];
const KEYED = ['HEADER', 'COOKIE', 'QS_ARGS'];
const OPS = [
  { value: 'eq', label: '等于' },
  { value: 'neq', label: '不等于' },
  { value: 'contain', label: '包含' },
  { value: 'not_contain', label: '不包含' },
  { value: 'starts_with', label: '开头是' },
  { value: 'ends_with', label: '结尾是' },
  { value: 'in', label: '在列表中' },
  { value: 'not_in', label: '不在列表中' },
  { value: 'rx', label: '正则' },
  { value: 'exists', label: '存在' },
  { value: 'not_exists', label: '不存在' },
];
const VALUELESS = ['exists', 'not_exists'];
const LISTS = ['in', 'not_in'];
const ACTIONS = [
  { value: 'block', label: '拦截', color: 'red' },
  { value: 'managed_challenge', label: '托管质询', color: 'orange' },
  { value: 'js_challenge', label: 'JS 质询', color: 'gold' },
  { value: 'ratelimit', label: '限速', color: 'purple' },
  { value: 'allow', label: '放行', color: 'green' },
  { value: 'log', label: '仅记录', color: 'default' },
];
const actionMeta = (a) => ACTIONS.find((x) => x.value === a) || { label: a, color: 'default' };
const fieldLabel = (f) => (FIELDS.find((x) => x.value === f) || { label: f }).label;
const opLabel = (o) => (OPS.find((x) => x.value === o) || { label: o }).label;

const summarize = (expr) => {
  const conds = expr?.conditions || [];
  if (!conds.length) return '全部请求';
  return conds
    .map((c) => {
      const f = fieldLabel(c.collection) + (c.key ? `[${c.key}]` : '');
      if (VALUELESS.includes(c.op)) return `${f} ${opLabel(c.op)}`;
      const v = Array.isArray(c.var) ? c.var.join(', ') : c.var;
      return `${f} ${opLabel(c.op)} ${v}`;
    })
    .join(expr.op === 'or' ? ' 或 ' : ' 且 ');
};

export default function GlobalRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const action = Form.useWatch('action', form);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await cdnService.getGlobalRules();
    if (res.success) setRules(res.data || []);
    else message.error(res.error || '加载失败');
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEditor = (rule) => {
    setEditing(rule || null);
    form.resetFields();
    if (rule) {
      const cfg = rule.action_config || {};
      form.setFieldsValue({
        name: rule.name,
        description: rule.description,
        priority: rule.priority,
        phase: rule.phase || 'pre',
        logic: rule.expression?.op === 'or' ? 'or' : 'and',
        conditions: (rule.expression?.conditions || []).map((c) => ({
          collection: c.collection,
          key: c.key,
          op: c.op,
          var: Array.isArray(c.var) ? c.var.join('\n') : c.var,
        })),
        action: rule.action,
        status: cfg.status || 403,
        requests: cfg.requests || 100,
        ttl: cfg.ttl || 60,
        key_by: cfg.key_by || 'ip',
        on_exceed: cfg.on_exceed || 'block',
        enabled: rule.enabled !== false,
      });
    } else {
      form.setFieldsValue({
        priority: 100, phase: 'pre', logic: 'and', action: 'block', status: 403,
        requests: 100, ttl: 60, key_by: 'ip', on_exceed: 'block', enabled: true,
        conditions: [{ collection: 'IP', op: 'in', var: '' }],
      });
    }
    setOpen(true);
  };

  const submit = async () => {
    const v = await form.validateFields();
    const conditions = (v.conditions || []).map((c) => {
      const cond = { collection: c.collection, op: c.op };
      if (KEYED.includes(c.collection) && c.key) cond.key = c.key.trim();
      if (!VALUELESS.includes(c.op)) {
        cond.var = LISTS.includes(c.op)
          ? String(c.var || '').split(/[\n,]/).map((x) => x.trim()).filter(Boolean)
          : String(c.var ?? '').trim();
      }
      return cond;
    });
    let action_config = {};
    if (v.action === 'block') action_config = { status: v.status };
    if (v.action === 'ratelimit') action_config = { requests: v.requests, ttl: v.ttl, key_by: v.key_by, on_exceed: v.on_exceed };
    setSaving(true);
    const res = await cdnService.saveGlobalRule({
      id: editing?.id,
      name: v.name,
      description: v.description || '',
      priority: v.priority,
      phase: v.phase,
      enabled: v.enabled,
      expression: { op: v.logic, conditions },
      action: v.action,
      action_config,
    });
    setSaving(false);
    if (res.success) {
      message.success('已保存，约 30 秒后在所有节点生效');
      setOpen(false);
      load();
    } else {
      message.error(res.error || res.message || '保存失败');
    }
  };

  const toggle = async (rule, enabled) => {
    const res = await cdnService.toggleGlobalRule(rule.id, enabled);
    if (res.success) {
      message.success(enabled ? '已启用' : '已停用');
      load();
    } else message.error(res.error || '操作失败');
  };

  const remove = async (rule) => {
    const res = await cdnService.deleteGlobalRule(rule.id);
    if (res.success) {
      message.success('已删除');
      load();
    } else message.error(res.error || '删除失败');
  };

  const columns = [
    { title: '优先级', dataIndex: 'priority', width: 80 },
    {
      title: '规则',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.name}</div>
          <div style={{ color: '#888', fontSize: 12 }}>{summarize(r.expression)}</div>
        </div>
      ),
    },
    {
      title: '动作', dataIndex: 'action', width: 110,
      render: (a) => { const m = actionMeta(a); return <Tag color={m.color}>{m.label}</Tag>; },
    },
    {
      title: '阶段', dataIndex: 'phase', width: 110,
      render: (p) => (p === 'post' ? <Tag>域规则之后</Tag> : <Tag color="blue">域规则之前</Tag>),
    },
    {
      title: '启用', dataIndex: 'enabled', width: 80,
      render: (en, r) => <Switch size="small" checked={en !== false} onChange={(v) => toggle(r, v)} />,
    },
    {
      title: '', width: 100,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditor(r)} />
          <Popconfirm title="删除这条全局规则？" onConfirm={() => remove(r)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="全局防火墙规则"
      extra={
        <Space>
          <Tooltip title="刷新"><Button icon={<ReloadOutlined />} onClick={load} /></Tooltip>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor(null)}>新建规则</Button>
        </Space>
      }
    >
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message="对所有域名生效，保存后约 30 秒同步到全部节点。优先级数值越小越先执行。" />
      <Table rowKey="id" loading={loading} columns={columns} dataSource={rules} pagination={false} size="middle" />

      <Modal
        title={editing ? '编辑全局规则' : '新建全局规则'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={submit}
        confirmLoading={saving}
        okText="保存"
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Space style={{ width: '100%' }} align="start">
            <Form.Item name="name" label="名称" rules={[{ required: true, message: '请填写名称' }]} style={{ width: 300 }}>
              <Input maxLength={100} placeholder="如：拦截已知扫描器" />
            </Form.Item>
            <Form.Item name="priority" label="优先级" rules={[{ required: true }]}>
              <InputNumber min={1} max={10000} style={{ width: 110 }} />
            </Form.Item>
            <Form.Item name="phase" label="执行时机">
              <Select style={{ width: 140 }} options={[{ value: 'pre', label: '域规则之前' }, { value: 'post', label: '域规则之后' }]} />
            </Form.Item>
            <Form.Item name="enabled" label="启用" valuePropName="checked"><Switch /></Form.Item>
          </Space>

          <Form.Item label="当请求满足" style={{ marginBottom: 8 }}>
            <Form.Item name="logic" noStyle>
              <Select style={{ width: 160 }} options={[{ value: 'and', label: '以下全部条件' }, { value: 'or', label: '以下任一条件' }]} />
            </Form.Item>
          </Form.Item>
          <Form.List name="conditions" rules={[{ validator: async (_, v) => { if (!v || !v.length) throw new Error('至少一个条件'); } }]}>
            {(fields, { add, remove: rm }, { errors }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Form.Item key={key} noStyle shouldUpdate>
                    {() => {
                      const c = form.getFieldValue(['conditions', name]) || {};
                      return (
                        <Space align="start" style={{ display: 'flex', marginBottom: 8 }} wrap>
                          <Form.Item name={[name, 'collection']} rules={[{ required: true }]} noStyle>
                            <Select style={{ width: 130 }} options={FIELDS} />
                          </Form.Item>
                          {KEYED.includes(c.collection) && (
                            <Form.Item name={[name, 'key']} rules={[{ required: true, message: '名称' }]} noStyle>
                              <Input style={{ width: 140 }} placeholder={c.collection === 'HEADER' ? '请求头名' : '名称'} />
                            </Form.Item>
                          )}
                          <Form.Item name={[name, 'op']} rules={[{ required: true }]} noStyle>
                            <Select style={{ width: 120 }} options={OPS} />
                          </Form.Item>
                          {!VALUELESS.includes(c.op) && (
                            <Form.Item name={[name, 'var']} rules={[{ required: true, message: '值' }]} noStyle>
                              {LISTS.includes(c.op)
                                ? <Input.TextArea style={{ width: 240 }} autoSize={{ minRows: 1, maxRows: 4 }} placeholder="每行一个" />
                                : <Input style={{ width: 240 }} placeholder="值" />}
                            </Form.Item>
                          )}
                          {c.collection === 'COUNTRY' && <CountryFlagList value={c.var} style={{ marginTop: 6 }} />}
                          <MinusCircleOutlined style={{ marginTop: 8, color: '#999' }} onClick={() => rm(name)} />
                        </Space>
                      );
                    }}
                  </Form.Item>
                ))}
                <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => add({ collection: 'URI', op: 'starts_with', var: '' })}>
                  添加条件
                </Button>
                <Form.ErrorList errors={errors} />
              </>
            )}
          </Form.List>

          <Space style={{ marginTop: 16 }} align="start" wrap>
            <Form.Item name="action" label="动作" rules={[{ required: true }]}>
              <Select style={{ width: 140 }} options={ACTIONS} />
            </Form.Item>
            {action === 'block' && (
              <Form.Item name="status" label="状态码">
                <InputNumber min={400} max={599} style={{ width: 100 }} />
              </Form.Item>
            )}
            {action === 'ratelimit' && (
              <>
                <Form.Item name="requests" label="请求数"><InputNumber min={1} style={{ width: 100 }} /></Form.Item>
                <Form.Item name="ttl" label="时间窗口(秒)"><InputNumber min={1} style={{ width: 110 }} /></Form.Item>
                <Form.Item name="key_by" label="按">
                  <Select style={{ width: 100 }} options={[{ value: 'ip', label: 'IP' }, { value: 'country', label: '国家' }, { value: 'uri', label: '路径' }]} />
                </Form.Item>
                <Form.Item name="on_exceed" label="超限后">
                  <Select style={{ width: 110 }} options={[{ value: 'block', label: '拦截' }, { value: 'challenge', label: '质询' }, { value: 'log', label: '仅记录' }]} />
                </Form.Item>
              </>
            )}
          </Space>
          <Form.Item name="description" label="备注">
            <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
